import { useState, useEffect, useRef } from 'react';
import ImagingOrders from '../../components/ImagingOrders';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';
import type { MarketplaceProvider } from '../../types';

const MODALITY_CHIPS = ['All', 'X-Ray', 'CT Scan', 'MRI', 'Ultrasound', 'ECG', 'Mammography', 'PET Scan'];

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconStar({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1.5">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function IconLocation({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconImaging({ color }: { color: string }) {
  return (
    <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1.5" fill={color} stroke="none" />
    </svg>
  );
}

function IconVerified({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill={color} stroke="none" style={{ flexShrink: 0 }}>
      <path d="M12 1l2.6 2.2 3.4-.3.7 3.3 2.9 1.8-1.5 3.1 1.5 3.1-2.9 1.8-.7 3.3-3.4-.3L12 23l-2.6-2.2-3.4.3-.7-3.3L2.4 16l1.5-3.1-1.5-3.1 2.9-1.8.7-3.3 3.4.3L12 1z" />
      <path d="M10.6 15.4l-2.8-2.8 1.1-1.1 1.7 1.7 4-4 1.1 1.1-5.1 5.1z" fill="#FFF" />
    </svg>
  );
}

function IconPhone({ color }: { color: string }) {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  );
}

/** The centre's published price list; it can arrive as JSON text. */
function parsePricing(raw: unknown): { service: string; price: number }[] {
  const rows = typeof raw === 'string'
    ? (() => { try { return JSON.parse(raw); } catch { return []; } })()
    : raw;
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r): r is { service: string; price: number } =>
      !!r && typeof r.service === 'string' && typeof r.price === 'number')
    .map((r) => ({ service: r.service, price: r.price }));
}

// ─── Modality helpers — derived from the provider's tags (Hospital Admin
// tags an imaging listing with the modalities it offers, e.g. "MRI") ───────

function modalitiesOf(p: MarketplaceProvider): string[] {
  return p.tags.filter((t) => MODALITY_CHIPS.includes(t));
}

function hasTag(p: MarketplaceProvider, tag: string): boolean {
  return p.tags.some((t) => t.toLowerCase() === tag.toLowerCase());
}

// ─── Center card ──────────────────────────────────────────────────────────────

function CenterCard({ center }: { center: MarketplaceProvider }) {
  const navigate = useNavigate();
  const modalities = modalitiesOf(center);
  const prices = parsePricing(center.pricing);

  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 16,
        padding: 16,
        boxShadow: T.shadow,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: '#4F46E512',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconImaging color="#4F46E5" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <p
              style={{
                fontFamily: T.font,
                fontSize: 14,
                fontWeight: 700,
                color: T.text,
                margin: '0 0 4px',
                lineHeight: 1.3,
              }}
            >
              {center.name}
            </p>
            {/* Only a centre the platform has actually verified says so. */}
            {center.isVerified && <IconVerified color={T.success} />}
          </div>
          {/* The centre's own words about itself. */}
          {center.description && (
            <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: '0 0 4px', lineHeight: 1.45 }}>
              {center.description}
            </p>
          )}
          {(center.address || center.city) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: T.font, fontSize: 11, color: T.textSec }}>
              <IconLocation color={T.textTer} />
              {[center.address, center.city].filter(Boolean).join(', ')}
            </div>
          )}
          {/* Hours as the centre published them. */}
          {center.openHours && (
            <div style={{ fontFamily: T.font, fontSize: 10.5, color: T.textTer, marginTop: 3 }}>
              {center.openHours}
            </div>
          )}
        </div>

        {/* Rating */}
        {center.rating > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              background: '#FFF7ED',
              borderRadius: 8,
              padding: '4px 8px',
              flexShrink: 0,
            }}
          >
            <IconStar color="#F59E0B" />
            <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#92400E' }}>
              {center.rating.toFixed(1)}
            </span>
          </div>
        )}
      </div>

      {/* Modality chips */}
      {modalities.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {modalities.map((mod) => (
            <span
              key={mod}
              style={{
                fontFamily: T.font,
                fontSize: 10,
                fontWeight: 600,
                color: '#4F46E5',
                background: '#4F46E510',
                border: '1px solid #4F46E520',
                borderRadius: 20,
                padding: '3px 8px',
              }}
            >
              {mod}
            </span>
          ))}
        </div>
      )}

      {/* Badges */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {hasTag(center, 'Emergency') && (
          <span
            style={{
              fontFamily: T.font,
              fontSize: 10,
              fontWeight: 700,
              color: '#DC2626',
              background: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: 20,
              padding: '3px 9px',
            }}
          >
            ⚡ Emergency
          </span>
        )}
        {hasTag(center, 'Home Portable') && (
          <span
            style={{
              fontFamily: T.font,
              fontSize: 10,
              fontWeight: 700,
              color: '#059669',
              background: '#ECFDF5',
              border: '1px solid #6EE7B7',
              borderRadius: 20,
              padding: '3px 9px',
            }}
          >
            🏠 Home Portable
          </span>
        )}
      </div>

      {/* What the centre charges, in its own published list. The app showed
          this and the web card did not, so the same centre looked priceless
          on one surface and priced on the other. */}
      {prices.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {prices.slice(0, 4).map((p) => (
            <div key={p.service} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '3px 0' }}>
              <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, minWidth: 0 }}>{p.service}</span>
              <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.text, whiteSpace: 'nowrap' }}>
                ₹{p.price.toLocaleString('en-IN')}
              </span>
            </div>
          ))}
          {prices.length > 4 && (
            <div style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, marginTop: 2 }}>
              +{prices.length - 4} more listed
            </div>
          )}
        </div>
      )}

      {/* The number itself, not just a button that dials it. */}
      {center.phone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <IconPhone color={T.textTer} />
          <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, flex: 1 }}>{center.phone}</span>
          <button
            onClick={() => { void navigator.clipboard?.writeText(center.phone as string); }}
            style={{
              fontFamily: T.font, fontSize: 10, fontWeight: 700, color: '#4F46E5',
              background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px',
            }}
          >
            Copy
          </button>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => navigate(`/patient/care-services/imaging/${center.slug || center.id}`)}
          style={{
            flex: 1,
            fontFamily: T.font,
            fontSize: 12,
            fontWeight: 700,
            color: '#4F46E5',
            background: '#4F46E510',
            border: '1.5px solid #4F46E530',
            borderRadius: 10,
            padding: '9px 0',
            cursor: 'pointer',
          }}
        >
          View Center
        </button>
        {center.phone ? (
          <a
            href={`tel:${center.phone}`}
            style={{
              flex: 1,
              fontFamily: T.font,
              fontSize: 12,
              fontWeight: 700,
              color: T.white,
              background: '#4F46E5',
              borderRadius: 10,
              padding: '9px 0',
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            Call Centre
          </a>
        ) : (
          <span style={{ flex: 1, fontFamily: T.font, fontSize: 11, color: T.textTer, textAlign: 'center', alignSelf: 'center' }}>
            No number listed
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ImagingDiscovery() {
  const [search, setSearch] = useState('');
  const [activeModality, setActiveModality] = useState('All');
  const [centers, setCenters] = useState<MarketplaceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params: Record<string, any> = { type: 'IMAGING', limit: 20 };
        const q = [search.trim(), activeModality !== 'All' ? activeModality : ''].filter(Boolean).join(' ');
        if (q) params.search = q;
        const res = await patientApi.getMarketplaceProviders(params);
        if (aliveRef.current) { setCenters(res.providers ?? []); setLoadError(false); }
      } catch {
        if (aliveRef.current) { setCenters([]); setLoadError(true); }
      } finally {
        if (aliveRef.current) setLoading(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, activeModality]);

  return (
    <div style={{ background: T.bg, fontFamily: T.font, maxWidth: 1080, margin: '0 auto', paddingBottom: 100 }}>
      {/* Header */}
      <div
        style={{
          background: `linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)`,
          padding: '20px 16px 56px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <h1 style={{ fontFamily: T.font, fontSize: 24, fontWeight: 800, color: T.white, margin: '0 0 6px' }}>
          Imaging & Diagnostics
        </h1>
        <p style={{ fontFamily: T.font, fontSize: 13, color: 'rgba(255,255,255,0.75)', margin: 0 }}>
          Your scans, and the centres that run them
        </p>
      </div>

      {/* The patient's REAL scans come first. This page used to open on a
          centre directory and a booking flow, while the studies a hospital had
          actually ordered lived on a page the patient had to go looking for. */}
      <div style={{ padding: '0 16px', marginTop: -40, position: 'relative', zIndex: 10 }}>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 14, boxShadow: T.shadow }}>
          <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 2 }}>
            Your scans
          </div>
          <div style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, marginBottom: 10, lineHeight: 1.5 }}>
            Ordered by your doctor or hospital. A scan is not booked here — once one is
            ordered it appears below, with its progress and report from the centre working it.
          </div>
          <ImagingOrders compact />
        </div>
      </div>

      <div style={{ padding: '18px 16px 0' }}>
        <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 800, color: T.text }}>
          Imaging centres
        </div>
        <div style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, marginTop: 2, lineHeight: 1.5 }}>
          Verified centres with the scans they list and what they charge. Call the
          centre to arrange a visit.
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '10px 16px 0', position: 'relative', zIndex: 10 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: T.white,
            borderRadius: 14,
            padding: '11px 14px',
            boxShadow: T.shadowLg ?? '0 4px 20px rgba(0,0,0,0.12)',
            border: `1px solid ${T.border}`,
          }}
        >
          <IconSearch color={T.textTer} />
          <input
            type="text"
            placeholder="Search center, scan type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontFamily: T.font,
              fontSize: 13,
              color: T.text,
              background: 'transparent',
              padding: 0,
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textTer, fontSize: 16, lineHeight: 1, padding: 0 }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Modality filter */}
      <div style={{ padding: '14px 16px 0', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
          {MODALITY_CHIPS.map((chip) => {
            const active = activeModality === chip;
            return (
              <button
                key={chip}
                onClick={() => setActiveModality(chip)}
                style={{
                  fontFamily: T.font,
                  fontSize: 12,
                  fontWeight: 600,
                  color: active ? T.white : T.textSec,
                  background: active ? '#4F46E5' : T.white,
                  border: `1px solid ${active ? '#4F46E5' : T.border}`,
                  borderRadius: 20,
                  padding: '7px 14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  boxShadow: T.shadow,
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {chip}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results */}
      <div style={{ padding: '20px 16px 0' }}>
        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec }}>Loading imaging centers…</p>
          </div>
        ) : centers.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: '#4F46E512',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px',
              }}
            >
              <IconImaging color="#4F46E5" />
            </div>
            <p style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>
              {loadError ? 'Could not load imaging centers' : 'No centers found'}
            </p>
            <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: 0 }}>
              {loadError ? 'Please check your connection and try again.' : 'Try a different search or modality filter.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 4px' }}>
              {centers.length} center{centers.length !== 1 ? 's' : ''} found
            </p>
            {centers.map((c) => (
              <CenterCard key={c.id} center={c} />
            ))}
          </div>
        )}
      </div>

      {/* NeoHealthCard branding */}
      <div style={{ padding: '20px 16px 8px', textAlign: 'center' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          NeoHealthCard™ · Verified imaging network · Scans are ordered by your doctor or hospital
        </span>
      </div>
    </div>
  );
}
